/**
 * Hooks for fetching geographic hierarchy filters (countries, provinces, organizations, activities).
 * Uses TanStack Query for caching and automatic refetching.
 */

import { useQuery } from '@tanstack/react-query'
import {
  fetchCountries,
  fetchProvinces,
  fetchOrganizations,
  fetchActivities,
  HttpError,
} from '../services/listSearchApi'
import type { Country, Province, Organization, Activity } from '../services/listSearchApi'

/**
 * Hook for fetching countries
 */
export function useCountries() {
  return useQuery<Country[], Error>({
    queryKey: ['geographicFilters', 'countries'],
    queryFn: ({ signal }) => fetchCountries(signal),
    staleTime: 10 * 60 * 1000, // 10 minutes - countries don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}

/**
 * Hook for fetching provinces, optionally filtered by country
 */
export function useProvinces(countryId?: number) {
  return useQuery<Province[], Error>({
    queryKey: ['geographicFilters', 'provinces', countryId],
    queryFn: ({ signal }) => fetchProvinces({ country_id: countryId, signal }),
    enabled: true, // Always fetch, but filter by country_id if provided
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}

/**
 * Hook for fetching organizations, optionally filtered by country, province, or location type
 */
export function useOrganizations(countryId?: number, provinceId?: number, locationType?: string) {
  return useQuery<Organization[], Error>({
    queryKey: ['geographicFilters', 'organizations', countryId, provinceId, locationType],
    queryFn: ({ signal }) =>
      fetchOrganizations({ country_id: countryId, province_id: provinceId, location_type: locationType, signal }),
    enabled: true,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}

/**
 * Hook for fetching activities, optionally filtered by organization
 */
export function useActivities(organizationId?: number) {
  return useQuery<Activity[], Error>({
    queryKey: ['geographicFilters', 'activities', organizationId],
    queryFn: ({ signal }) => fetchActivities({ organization_id: organizationId, signal }),
    enabled: true,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}

/** Catalogue complet des activités (filtres organisations — été / hiver). */
export function useAllActivities() {
  return useQuery<Activity[], Error>({
    queryKey: ['geographicFilters', 'activities', 'all'],
    queryFn: ({ signal }) => fetchActivities({ all: true, signal }),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}
